import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import axios from "axios";
import { Moon, Sun } from "lucide-react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { Marquee } from "@/components/site/Marquee";
import { Manifesto } from "@/components/site/Manifesto";
import { Showcase } from "@/components/site/Showcase";
import { DownloadSection } from "@/components/site/DownloadSection";
import { Footer } from "@/components/site/Footer";
import Admin from "@/pages/Admin";
import Help from "@/pages/Help";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import IpoTrackerGuide from "@/pages/IpoTrackerGuide";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "";

const getInitialTheme = () => {
  const savedTheme = localStorage.getItem("yiis-theme");
  if (savedTheme === "light" || savedTheme === "dark") return savedTheme;
  return "dark";
};

const adminHeaders = () => {
  const token = localStorage.getItem("yiis-admin-token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Landing = ({ maintenancePreview = false }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [downloads, setDownloads] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [downloadAvailable, setDownloadAvailable] = useState(null);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("yiis-theme", theme);
  }, [theme]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (!API || maintenancePreview) return undefined;
    if (sessionStorage.getItem("yiis-visited")) return undefined;
    let cancelled = false;
    let retryTimer;
    const logVisit = async () => {
      try {
        await axios.post(`${API}/stats/visit`);
        if (!cancelled) sessionStorage.setItem("yiis-visited", "1");
      } catch (e) {
        if (!cancelled) retryTimer = window.setTimeout(logVisit, 3000);
      }
    };
    logVisit();
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [maintenancePreview]);

  const refreshStats = useCallback(async () => {
    if (!API) return;
    const config = { headers: adminHeaders() };
    try {
      const res = await axios.get(`${API}/stats/downloads`, config);
      setDownloads(res.data.total);
    } catch (e) {
      /* counter stays as-is */
    }
    try {
      const res = await axios.get(`${API}/stats/visitors`, config);
      setVisitors(res.data.unique_visitors);
    } catch (e) {
      /* counter stays as-is */
    }
  }, []);

  useEffect(() => {
    if (!API) return undefined;
    let cancelled = false;
    let retryTimer;
    const refreshBackend = async () => {
      let connected = false;
      if (maintenancePreview) {
        setDownloadAvailable(false);
        connected = true;
      } else {
        try {
          const res = await axios.get(`${API}/download/status`);
          if (!cancelled) setDownloadAvailable(res.data.available === true);
          connected = true;
        } catch (e) {
          /* keep the connecting state until the backend responds */
        }
      }
      await refreshStats();
      if (!cancelled) retryTimer = window.setTimeout(refreshBackend, connected ? 15000 : 3000);
    };
    refreshBackend();
    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
    };
  }, [maintenancePreview, refreshStats]);

  const handleDownload = useCallback(() => {
    if (!API || maintenancePreview || downloadAvailable !== true) return;
    window.location.assign(`${API}/download/yiis-100`);
    setTimeout(refreshStats, 2500);
  }, [downloadAvailable, maintenancePreview, refreshStats]);

  return (
    <div className="App">
      {maintenancePreview && (
        <div className="fixed top-0 inset-x-0 z-[100] bg-amber-500 text-black text-center px-4 py-2 font-mono text-[10px] md:text-xs tracking-[0.14em] uppercase font-bold">
          Admin preview — public visitors currently see the maintenance screen
        </div>
      )}
      <div className="grain-overlay" aria-hidden="true" />
      <Header
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        onDownload={handleDownload}
        downloadAvailable={downloadAvailable}
      />
      <main>
        <Hero downloads={downloads} visitors={visitors} onDownload={handleDownload} downloadAvailable={downloadAvailable} />
        <Marquee />
        <Manifesto />
        <Showcase />
        <DownloadSection downloads={downloads} onDownload={handleDownload} downloadAvailable={downloadAvailable} />
      </main>
      <Footer />
    </div>
  );
};

const StatusScreen = ({ connecting, message }) => {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("yiis-theme", theme);
  }, [theme]);

  const nextTheme = theme === "dark" ? "light" : "dark";

  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 py-12">
      <div className="grain-overlay" aria-hidden="true" />
      <button
        type="button"
        onClick={() => setTheme(nextTheme)}
        aria-label={`Switch to ${nextTheme} mode`}
        title={`Switch to ${nextTheme} mode`}
        data-testid="status-theme-toggle-button"
        className="fixed top-5 right-5 z-[110] h-10 flex items-center gap-2 px-4 border border-border bg-background/80 backdrop-blur font-mono text-[10px] tracking-[0.14em] uppercase hover:border-accent hover:text-accent transition-colors"
      >
        {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        {theme === "dark" ? "Light" : "Dark"}
      </button>
      <section className="relative max-w-2xl w-full border border-border bg-background p-8 md:p-14">
        <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent mb-6">
          YKD ENTERPRISES / YIIS-100
        </div>
        <h1 className="font-display font-semibold tracking-tight text-4xl md:text-6xl mb-6">
          {connecting ? "CONNECTING" : "SCHEDULED MAINTENANCE"}
        </h1>
        <p className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-xl">
          {connecting ? "Checking site availability. This may take a moment." : message}
        </p>
        {!connecting && (
          <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
              Public access is temporarily paused
            </span>
            <Link
              to="/admin"
              className="w-fit px-6 py-3 border border-border font-mono text-xs tracking-[0.18em] uppercase hover:border-accent hover:text-accent transition-colors"
            >
              Admin Login
            </Link>
          </div>
        )}
      </section>
    </main>
  );
};
const PublicGate = ({ children }) => {
  const [status, setStatus] = useState({ loading: true, maintenance_mode: false, admin_access: false, message: "" });

  useEffect(() => {
    if (!API) return undefined;
    let cancelled = false;
    let timer;
    const check = async () => {
      try {
        const res = await axios.get(`${API}/site/status`, { headers: adminHeaders() });
        if (!cancelled) {
          setStatus({ loading: false, ...res.data });
          timer = window.setTimeout(check, 15000);
        }
      } catch (e) {
        if (!cancelled) timer = window.setTimeout(check, 3000);
      }
    };
    check();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    if (!robots) return undefined;
    const original = robots.getAttribute("content") || "index,follow";
    robots.setAttribute("content", status.maintenance_mode ? "noindex,nofollow" : "index,follow");
    return () => robots.setAttribute("content", original);
  }, [status.maintenance_mode]);

  if (status.loading) return <StatusScreen connecting />;
  if (status.maintenance_mode && !status.admin_access) {
    return <StatusScreen message={status.message} />;
  }
  return children(status.maintenance_mode && status.admin_access);
};

const RoutedApp = () => {
  const location = useLocation();
  if (location.pathname === "/admin" || location.pathname === "/admin/") {
    return <Routes><Route path="/admin/*" element={<Admin />} /></Routes>;
  }
  return (
    <PublicGate>
      {(maintenancePreview) => (
        <Routes>
          <Route path="/" element={<Landing maintenancePreview={maintenancePreview} />} />
          <Route path="/help" element={<Help />} />
          <Route path="/ipo-portfolio-tracker" element={<IpoTrackerGuide />} />
          <Route path="/privacy" element={<Legal type="privacy" />} />
          <Route path="/terms" element={<Legal type="terms" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      )}
    </PublicGate>
  );
};

function App() {
  return (
    <BrowserRouter>
      <RoutedApp />
    </BrowserRouter>
  );
}

export default App;
